import { memo, useEffect } from "react";
import { useMutation, useQuery } from "react-query";
import { Button, DatePicker, Flex, Form, Input, Select, Skeleton, Spin, Switch } from "antd";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ObjectHelper } from "../../utils/ObjectHelper";
import { mdlUserCreateRequest } from "../../models/service-models/user/UserCreateRequest";
import { mdlUserUpdateRequest } from "../../models/service-models/user/UserUpdateRequest";
import { mdlUserDetailRequest } from "../../models/service-models/user/UserDetailRequest";
import { UserService } from "../../services/UserService";
import dayjs, { Dayjs } from "dayjs";
import { enmTitle } from "../../models/enums/Title";
import { SFDatePicker } from "../common/DatePicker";

export type UserDetailProps = {
    userId?: string;
    hotelId?: string;
    callback?: Function;
}


const UserDetail = (props: UserDetailProps) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    useEffect(() => {
        if (!props.userId && form) {
            form.resetFields(); // ID yoksa formu sıfırla
        }
    }, [props.userId, form]);

    const {
        data,
        isLoading,
        isFetched,
        refetch
    } = useQuery(
        ["getUserDetail", props.userId],
        () => {
            const request: mdlUserDetailRequest = { id: props.userId!, hotelId: props.hotelId! };
            return UserService.Detail(request);
        },
        {
            staleTime: 0,
            refetchOnWindowFocus: false,
            enabled: !!props.userId,
        }
    );
    useEffect(() => {
        if (data && form) {
            form.setFieldsValue({
                userName: data?.body?.data?.userName,
                title: data?.body?.data?.title,
                firstName: data?.body?.data?.firstName,
                lastName: data?.body?.data?.lastName,
                birthDate: data?.body?.data?.birthDate ? dayjs(data.body.data.birthDate) : null,
                isActive: data?.body?.data?.isActive,
                phone: data?.body?.data?.phone,
                mobile: data?.body?.data?.mobilePhone,
                email: data?.body?.data?.email,
                validDate: data?.body?.data?.validDate ? dayjs(data.body.data.validDate) : null
            });
        }
    }, [data, form]);



    const { mutateAsync: saveUser, isLoading: mutateLoading } = useMutation(
        (data: mdlUserCreateRequest & mdlUserUpdateRequest) =>
            props.userId
                ? UserService.Update(data as mdlUserUpdateRequest)
                : UserService.Create(data as mdlUserCreateRequest),
        {
            onMutate(variables) {
                variables.hotelId = props.hotelId;
                variables.id = props.userId!;
            },
            onError: (error) => {
                console.log(error);
            },
            onSuccess: (data) => {
                if (data.header && data.header.success && data.body?.isSuccessful) {
                    toast.success(t("common.success"));
                    form.resetFields();
                    props.callback && props.callback();
                } else {
                    toast.error(data.body?.message || ObjectHelper.getErrorMessage(data.header.messages));
                }
            }
        }
    );

    return (
        <>
            {!isLoading
                ?
                ((props.userId && isFetched) || !props.userId)
                && (
                    <Spin spinning={mutateLoading}>
                        <Form
                            form={form}
                            initialValues={{
                                userName: data?.body?.data?.userName,
                                title: data?.body?.data?.title,
                                firstName: data?.body?.data?.firstName,
                                lastName: data?.body?.data?.lastName,
                                birthDate: data?.body?.data?.birthDate ? dayjs(data.body.data.birthDate) : null,
                                isActive: data?.body?.data?.isActive,
                                phone: data?.body?.data?.phone,
                                mobile: data?.body?.data?.mobilePhone,
                                email: data?.body?.data?.email,
                                validDate: data?.body?.data?.validDate ? dayjs(data.body.data.validDate) : null
                            }}
                            name="user-form"
                            layout="vertical"
                            onFinish={async (values) => {
                                const formattedValues = {
                                    ...values,
                                };
                                await saveUser(formattedValues);
                            }}
                            autoComplete="off"
                        >
                            <Form.Item
                                label={t("definitions.users.userName")}
                                name="userName"
                                rules={[
                                    { required: true, message: t("common.required") },
                                    { type: "string", message: t("definitions.users.invalidUserName") },
                                ]}
                            >
                                <Input disabled={props.userId != null} />
                            </Form.Item>

                            <Form.Item
                                label={t("definitions.users.formTitle")}
                                initialValue={Object.values(enmTitle).at(0)}
                                name="title"
                            >
                                <Select>
                                    {Object.values(enmTitle).map((value) => (
                                        <Select.Option key={value} value={value}>
                                            {value}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label={t("definitions.users.lastName")}
                                name="lastName">
                                <Input />
                            </Form.Item>
                            <Form.Item
                                label={t("definitions.users.firstName")}
                                name="firstName">
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t("definitions.users.birthDate")}
                                name="birthDate">
                                <SFDatePicker
                                    onChange={(date: Dayjs) => {
                                        if (date) {
                                            form.setFieldValue("birthDate", new Date(
                                                Date.UTC(
                                                    date.year(),
                                                    date.month(),
                                                    date.date()
                                                )
                                            ));
                                        } else {
                                            form.setFieldValue("birthDate", null);
                                        }
                                    }} />
                            </Form.Item>

                            <Form.Item
                                label={t("definitions.users.isActive")}
                                name="isActive"
                                valuePropName="checked">
                                <Switch />
                            </Form.Item>

                            <Form.Item
                                label={t("definitions.users.phone")}
                                initialValue={'+90'}
                                name="phone">
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t("definitions.users.mobile")}
                                initialValue={'+90'}
                                name="mobile">
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t("definitions.users.email")}
                                name="email"
                                rules={[
                                    { required: true, message: t("common.required") },
                                    { type: "email", message: t("definitions.users.invalidEmail") },
                                ]}>
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t("definitions.users.validThrough")}
                                name="validDate">
                                <SFDatePicker
                                    onChange={(date: Dayjs) => {
                                        if (date) {
                                            form.setFieldValue("validDate", new Date(
                                                Date.UTC(
                                                    date.year(),
                                                    date.month(),
                                                    date.date()
                                                )
                                            ));
                                        } else {
                                            form.setFieldValue("validDate", null);
                                        }
                                    }} />
                            </Form.Item>

                            <Form.Item>
                                <Flex align={"center"} justify="flex-end">
                                    <Button type="primary" htmlType="submit">
                                        {t("common.save")}
                                    </Button>
                                </Flex>
                            </Form.Item>
                        </Form>
                    </Spin>
                ) : <>
                    <Skeleton active />
                    <Skeleton active />
                    <Skeleton active />
                </>}
        </>
    );
};

export default memo(UserDetail);
